import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { Statement, JobResponse, IncomeProfile } from '../types';

export interface GapTxn {
  id: number;
  statement_id: number;
  date: string | null;
  description: string | null;
  amount: number | null;
  transaction_type: string;
  property_status: string | null;
  manual_override: boolean;
  override_reason: string | null;
}

interface ReconResult {
  reconciliation_status: string;
  reconciliation_gap: number | null;
}

export interface AccountData {
  bank_label: string;
  owner_type: string;
  account_type: string;
  latest_balance: number | null;
  latest_date: string | null;
  total_transactions: number;
  statement_count: number;
  earliest_date: string | null;
  latest_statement_date: string | null;
  recon_counts: Record<string, number>;
  source_party: 'client' | 'opposing' | 'third_party' | 'joint' | null;
}

export function useStatements(caseId: string | undefined) {
  return useQuery<Statement[]>({
    queryKey: ['statements', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/statements`);
      return data.statements;
    },
    enabled: !!caseId,
  });
}

/** Directed reissue/successor edge (P4). Optional on the wire — older servers
 * omit it and the UI degrades to no chain chips. */
export interface AccountSuccessionInfo {
  predecessor_bank_label: string;
  successor_bank_label: string;
  succession_date: string | null;
  reason: string;
}

export interface AccountsResponse {
  accounts: AccountData[];
  successions: AccountSuccessionInfo[];
}

export function useAccounts(caseId: string | undefined) {
  return useQuery<AccountsResponse>({
    queryKey: ['accounts', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/accounts`);
      return {
        accounts: data.accounts ?? [],
        successions: Array.isArray(data.successions) ? data.successions : [],
      };
    },
    enabled: !!caseId,
  });
}

export interface CoverageOverlap {
  kind: 'overlap';
  bank_label: string;
  statement_ids: number[];
  source_files: (string | null)[];
  source_parties: (string | null)[];
  overlap_start: string;
  overlap_end: string;
  overlap_days: number;
  contained: boolean;
  classification: 'redundant' | 'conflicting' | 'unclassified';
  double_counted_transactions: number | null;
  double_counted_net: number | null;
  severity: 'medium' | 'high';
}

export interface CoverageChainFinding {
  kind: 'continuity_break' | 'quantified_gap' | 'unquantified_gap';
  bank_label: string;
  statement_ids: number[];
  source_files: (string | null)[];
  junction?: [string, string];
  gap_start?: string;
  gap_end?: string;
  prev_ending_balance?: number;
  next_beginning_balance?: number;
  balance_delta: number | null;
  delta_phrase: string;
  severity: 'low' | 'medium' | 'high';
  detail: string;
}

export interface AccountCoverage {
  bank_label: string;
  account_type: string;
  owner_type: string;
  last4: string | null;
  statement_count: number;
  excluded_unverified: number;
  excluded_undated: number;
  excluded_implausible: number;
  overlaps: CoverageOverlap[];
  chain: CoverageChainFinding[];
}

export interface CoverageReport {
  accounts: AccountCoverage[];
  totals: {
    overlaps: number;
    conflicting_overlaps: number;
    continuity_breaks: number;
    quantified_gaps: number;
    unquantified_gaps: number;
  };
}

/** Case-wide coverage integrity (period overlaps + balance-continuity),
 *  computed live server-side by server/continuity.py. */
export function useCoverageReport(caseId: string | undefined) {
  return useQuery<CoverageReport>({
    queryKey: ['coverage-report', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/coverage-report`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useReclassifyCreditCards(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/cases/${caseId}/reclassify-credit-cards`);
      return data as { converted_count: number; converted: { asset_id: number; name: string; balance: number | null }[] };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', caseId] });
      queryClient.invalidateQueries({ queryKey: ['liabilities', caseId] });
      queryClient.invalidateQueries({ queryKey: ['net-value-split', caseId] });
    },
  });
}

export function useUpdateAccount(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ bankLabel, ownerType, accountType, propertyStatus }: {
      bankLabel: string;
      ownerType: string;
      accountType: string;
      propertyStatus: string;
    }) => {
      const { data } = await api.put(`/cases/${caseId}/accounts/${encodeURIComponent(bankLabel)}`, {
        owner_type: ownerType,
        account_type: accountType,
        property_status: propertyStatus,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['discovery-leads', caseId] });
    },
  });
}

export function useMergeAccounts(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sourceBankLabels, targetBankLabel }: {
      sourceBankLabels: string[];
      targetBankLabel: string;
    }) => {
      const { data } = await api.post(`/cases/${caseId}/accounts/merge`, {
        source_bank_labels: sourceBankLabels,
        target_bank_label: targetBankLabel,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['discovery-leads', caseId] });
    },
  });
}

export interface DuplicateAccountGroup {
  target: string;
  sources: string[];
  statement_count: number;
}

export function useDuplicateAccounts(caseId: string | undefined) {
  return useQuery<DuplicateAccountGroup[]>({
    queryKey: ['duplicate-accounts', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/accounts/duplicates`);
      return data.groups as DuplicateAccountGroup[];
    },
    enabled: !!caseId,
  });
}

export function useCombineDuplicateAccounts(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ status: string; groups_merged: number; statements_updated: number }, Error>({
    mutationFn: async () => {
      const { data } = await api.post(`/cases/${caseId}/accounts/combine-duplicates`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['duplicate-accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['discovery-leads', caseId] });
    },
  });
}

export function useMoveStatement(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ status: string; statement_id: number }, Error, { statementId: number; targetBankLabel: string }>({
    mutationFn: async ({ statementId, targetBankLabel }) => {
      const { data } = await api.patch(`/statements/${statementId}/account`, {
        target_bank_label: targetBankLabel,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['duplicate-accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['discovery-leads', caseId] });
      queryClient.invalidateQueries({ queryKey: ['transactions', caseId] });
    },
  });
}

export function useDeleteStatement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (statementId: number) => {
      const { data } = await api.delete(`/statements/${statementId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statements'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['discovery-leads'] });
    },
  });
}

/** User-entered Bates range for a pre-stamped production (per FILE, never per
 *  batch — two productions cannot share a range). Detection still runs
 *  server-side; a typed range is authoritative. */
export interface BatesRangeInput {
  start: string;
  end?: string;
}

function appendBates(formData: FormData, bates?: BatesRangeInput | null) {
  if (bates?.start.trim()) {
    formData.append('bates_start', bates.start.trim());
    if (bates.end?.trim()) formData.append('bates_end', bates.end.trim());
  }
}

export async function uploadFile(
  caseId: string, file: File, sourceParty?: SourceParty | null,
  bates?: BatesRangeInput | null, productionRoundId?: number | null,
) {
  const formData = new FormData();
  formData.append('file', file);
  if (sourceParty) formData.append('source_party', sourceParty);
  appendBates(formData, bates);
  if (productionRoundId != null) formData.append('production_round_id', String(productionRoundId));
  const { data } = await api.post(`/cases/${caseId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export interface DetectedBoundary {
  start_page: number;
  end_page: number;
  bank_hint: string | null;
  period_hint: string | null;
  account_last4: string | null;
  confidence: number;
  fingerprint: string | null;
  is_new_format: boolean;
}

export interface BundleDetectResponse {
  status: 'single_statement' | 'review_required' | 'auto_accepted' | 'skipped' | 'intake_queued' | 'queued' | 'scanning';
  bundle_id?: number;
  page_count?: number;
  filename?: string;
  boundaries?: DetectedBoundary[];
  statements_detected?: number;
  statements_created?: number;
  job_ids?: number[];
  job_id?: number;
  new_format_confirmations?: number;
  reason?: string;
  message?: string;
  existing?: unknown[];
}

export async function detectBundle(
  caseId: string, file: File, sourceParty?: SourceParty | null,
  bates?: BatesRangeInput | null, productionRoundId?: number | null,
): Promise<BundleDetectResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (sourceParty) formData.append('source_party', sourceParty);
  appendBates(formData, bates);
  if (productionRoundId != null) formData.append('production_round_id', String(productionRoundId));
  const { data } = await api.post(`/cases/${caseId}/bundles/detect`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export interface JobProgress {
  job_id: number;
  status: string;
  progress: number;
  progress_message: string | null;
  pages_done: number;
  pages_failed: number;
  pages_total: number | null;
  cost_so_far_usd: number | null;
  elapsed_seconds: number | null;
  avg_seconds_per_page: number | null;
  eta_seconds: number | null;
}

export function useJobProgress(jobId: number | null, enabled: boolean) {
  return useQuery<JobProgress>({
    queryKey: ['jobProgress', jobId],
    queryFn: async () => {
      const { data } = await api.get(`/jobs/${jobId}/progress`);
      return data;
    },
    enabled: enabled && jobId !== null,
    refetchInterval: 2000,
    refetchIntervalInBackground: false,
  });
}

export async function getBundle(bundleId: number): Promise<{
  bundle_id: number; case_id: number; filename: string; page_count: number;
  status: string; boundaries: DetectedBoundary[];
}> {
  const { data } = await api.get(`/bundles/${bundleId}`);
  return data;
}

export async function confirmBundle(
  bundleId: number,
  boundaries: Array<{ start_page: number; end_page: number; kept: boolean }>,
): Promise<{ status: string; bundle_id: number; statements_created: number; job_ids: number[]; new_format_confirmations: number }> {
  const { data } = await api.post(`/bundles/${bundleId}/confirm`, { boundaries });
  return data;
}

export function useJobStatus(jobId: number | null, enabled: boolean = true) {
  return useQuery<JobResponse>({
    queryKey: ['job', jobId],
    queryFn: async () => {
      const { data } = await api.get(`/jobs/${jobId}`);
      return data;
    },
    enabled: !!jobId && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return 2000;
    },
  });
}

export function useCaseJobs(caseId: string | undefined, statuses?: string) {
  return useQuery<JobResponse[]>({
    queryKey: ['caseJobs', caseId, statuses],
    queryFn: async () => {
      const params = statuses ? `?status=${statuses}` : '';
      const { data } = await api.get(`/cases/${caseId}/jobs${params}`);
      return data.jobs;
    },
    enabled: !!caseId,
    refetchInterval: (query) => {
      const jobs = query.state.data;
      if (!jobs) return 5000;
      const hasActive = jobs.some(j => j.status === 'pending' || j.status === 'processing');
      return hasActive ? 3000 : false;
    },
  });
}

export function useRetryJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: number) => {
      const { data } = await api.post(`/jobs/${jobId}/retry`);
      return data;
    },
    onSuccess: (data: JobResponse) => {
      queryClient.invalidateQueries({ queryKey: ['job', data.id] });
      queryClient.invalidateQueries({ queryKey: ['caseJobs'] });
    },
  });
}

export function useForceResetJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: number) => {
      const { data } = await api.post(`/jobs/${jobId}/force-reset`);
      return data;
    },
    onSuccess: (data: JobResponse) => {
      queryClient.invalidateQueries({ queryKey: ['job', data.id] });
      queryClient.invalidateQueries({ queryKey: ['caseJobs'] });
    },
  });
}

export function useDeleteJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: number) => {
      const { data } = await api.delete(`/jobs/${jobId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseJobs'] });
    },
  });
}

export function useDeleteAllFailedJobs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (caseId: string) => {
      const { data } = await api.delete(`/cases/${caseId}/jobs/failed`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseJobs'] });
    },
  });
}


export function useStatementTransactions(statementId: number | null) {
  return useQuery<GapTxn[]>({
    queryKey: ['statement-txns', statementId],
    queryFn: async () => {
      const { data } = await api.get(`/statements/${statementId}/transactions`);
      return data.transactions;
    },
    enabled: statementId !== null,
  });
}

export function usePatchBalances(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<ReconResult & { beginning_balance: number | null; ending_balance: number | null }, Error, { statementId: number; beginning_balance?: number; ending_balance?: number }>({
    mutationFn: async ({ statementId, ...body }) => {
      const { data } = await api.patch(`/statements/${statementId}/balances`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
    },
  });
}

export function useAddManualTransaction(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<ReconResult & { transaction: GapTxn }, Error, { statementId: number; date: string; description: string; amount: number; property_status?: string }>({
    mutationFn: async ({ statementId, ...body }) => {
      const { data } = await api.post(`/statements/${statementId}/transactions`, body);
      return data;
    },
    onSuccess: (_data, { statementId }) => {
      queryClient.invalidateQueries({ queryKey: ['statement-txns', statementId] });
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['transactions', caseId] });
    },
  });
}

export function useEditTransaction(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<ReconResult & { transaction: GapTxn }, Error, { transactionId: number; statementId: number; date?: string; description?: string; amount?: number }>({
    mutationFn: async ({ transactionId, statementId: _sid, ...body }) => {
      const { data } = await api.patch(`/transactions/${transactionId}/edit`, body);
      return data;
    },
    onSuccess: (_data, { statementId }) => {
      queryClient.invalidateQueries({ queryKey: ['statement-txns', statementId] });
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['transactions', caseId] });
    },
  });
}

export function useDeleteGapTransaction(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<ReconResult & { transaction_id: number }, Error, { transactionId: number; statementId: number }>({
    mutationFn: async ({ transactionId }) => {
      const { data } = await api.delete(`/transactions/${transactionId}`);
      return data;
    },
    onSuccess: (_data, { statementId }) => {
      queryClient.invalidateQueries({ queryKey: ['statement-txns', statementId] });
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['transactions', caseId] });
    },
  });
}

export function useCloseGap(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<ReconResult, Error, { statementId: number; note: string }>({
    mutationFn: async ({ statementId, note }) => {
      const { data } = await api.post(`/statements/${statementId}/close-gap`, { note });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
    },
  });
}

interface CleanDuplicatesResult {
  message: string;
  deleted: number;
  primary_count: number;
  secondary_count: number;
  reconciliation_status: string;
  reconciliation_gap: number | null;
}

export interface DiscoveryLeadTransaction {
  date: string | null;
  description: string;
  amount: number;
  source_bank_label: string;
  statement_id: number | null;
  source_page: number | null;
  source_file: string;
  raw_text: string;
  confidence_score: number | null;
}

export interface DiscoveryLead {
  account_type: string;
  last4: string;
  label: string;
  total_amount: number;
  transfer_count: number;
  first_date: string | null;
  last_date: string | null;
  transactions: DiscoveryLeadTransaction[];
}

export function useDiscoveryLeads(caseId: string | undefined) {
  return useQuery<DiscoveryLead[]>({
    queryKey: ['discovery-leads', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/discovery-leads`);
      return data.leads;
    },
    enabled: !!caseId,
  });
}

export function useCleanDuplicates(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<CleanDuplicatesResult, Error, { statementId: number }>({
    mutationFn: async ({ statementId }) => {
      const { data } = await api.post(`/statements/${statementId}/clean-duplicates`);
      return data;
    },
    onSuccess: (_data, { statementId }) => {
      queryClient.invalidateQueries({ queryKey: ['statement-txns', statementId] });
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['transactions', caseId] });
    },
  });
}

export type SourceParty = 'client' | 'opposing' | 'third_party' | 'joint' | null;

export function useUpdateStatementParty(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ status: string }, Error, { statementId: number; source_party: SourceParty }>({
    mutationFn: async ({ statementId, source_party }) => {
      const { data } = await api.patch(`/statements/${statementId}/party`, { source_party });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
    },
  });
}

export function useUpdateAccountParty(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ status: string }, Error, { bankLabel: string; source_party: SourceParty }>({
    mutationFn: async ({ bankLabel, source_party }) => {
      const { data } = await api.patch(`/cases/${caseId}/accounts/${encodeURIComponent(bankLabel)}/party`, { source_party });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['transactions', caseId] });
    },
  });
}

export function useAutoAssignParties(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ status: string; assigned: number; skipped: number; total: number }, Error>({
    mutationFn: async () => {
      const { data } = await api.post(`/cases/${caseId}/auto-assign-parties`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['transactions', caseId] });
    },
  });
}

export interface IntakeProposal {
  id: number;
  case_id: number;
  doc_type: string;
  confidence: number;
  status: string;
  payload: Record<string, unknown>;
  // Server-computed: confirm will record real income (vs file-only).
  has_income_fact?: boolean;
  source_filename: string | null;
  source_content_type: string | null;
  source_job_id: number | null;
  classifier_note: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export function useIntakeProposals(caseId: string, status: string = 'pending') {
  return useQuery<IntakeProposal[]>({
    queryKey: ['intakeProposals', caseId, status],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/intake/proposals`, { params: { status } });
      return data.proposals as IntakeProposal[];
    },
    refetchInterval: 5000,
  });
}

export function useConfirmProposal(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    IntakeProposal,
    Error,
    {
      proposalId: number;
      applyFields?: string[];
      spouse?: string;
      incomeOverrides?: Record<string, unknown>;
    }
  >({
    mutationFn: async ({ proposalId, applyFields, spouse, incomeOverrides }) => {
      const body: {
        apply_fields?: string[];
        spouse?: string;
        income_overrides?: Record<string, unknown>;
      } = {};
      if (applyFields) body.apply_fields = applyFields;
      if (spouse) body.spouse = spouse;
      if (incomeOverrides) body.income_overrides = incomeOverrides;
      const { data } = await api.post(`/intake/proposals/${proposalId}/confirm`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intakeProposals', caseId] });
      queryClient.invalidateQueries({ queryKey: ['cases', caseId] });
      queryClient.invalidateQueries({ queryKey: ['caseIncome', caseId] });
      queryClient.invalidateQueries({ queryKey: ['assets', caseId] });
    },
  });
}

export function useIncomeProfiles(caseId: string) {
  return useQuery<IncomeProfile[]>({
    queryKey: ['caseIncome', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/income`);
      return data.profiles as IncomeProfile[];
    },
    enabled: !!caseId,
  });
}

export function useDismissProposal(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<IntakeProposal, Error, number>({
    mutationFn: async (proposalId) => {
      const { data } = await api.post(`/intake/proposals/${proposalId}/dismiss`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intakeProposals', caseId] });
    },
  });
}

export function useReclassifyProposal(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<IntakeProposal, Error, { proposalId: number; doc_type: string }>({
    mutationFn: async ({ proposalId, doc_type }) => {
      const { data } = await api.post(`/intake/proposals/${proposalId}/reclassify`, { doc_type });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intakeProposals', caseId] });
    },
  });
}
